import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Loader2, MessageSquare, Upload, Download, FileCheck2, FileText, Smartphone, QrCode, RefreshCw, CheckCircle2, XCircle, Zap } from "lucide-react";
import api, { formatDetail, fmtINR, shortDate, BACKEND_URL } from "@/lib/api";
import Modal from "@/components/Modal";

export function SmsWhitelist() {
    const [items, setItems] = useState([]);
    const [logs, setLogs] = useState([]);
    const [open, setOpen] = useState(false);
    const [creds, setCreds] = useState(null);
    const [apk, setApk] = useState(null);
    const [showQr, setShowQr] = useState(false);

    const [pairing, setPairing] = useState(null); // { pin, pairing_id }
    const [importBusy, setImportBusy] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [testBusy, setTestBusy] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const xmlRef = useRef(null);

    const load = async () => {
        const [w, l, c, a] = await Promise.all([
            api.get("/sms/whitelist"), api.get("/sms/logs"),
            api.get("/partner/api-credentials"), api.get("/sms/apk/info"),
        ]);
        setItems(w.data.items); setLogs(l.data.items); setCreds(c.data); setApk(a.data);
    };
    useEffect(() => { load(); }, []);
    const remove = async (id) => { await api.delete(`/sms/whitelist/${id}`); load(); };

    const testWebhook = async () => {
        setTestBusy(true); setTestResult(null);
        try {
            const r = await api.post("/sms/test-webhook");
            setTestResult(r.data);
            await load();   // refresh logs to show the synthetic test SMS
        } catch (e) {
            setTestResult({ ok: false, hint: formatDetail(e?.response?.data?.detail) || e.message });
        } finally {
            setTestBusy(false);
        }
    };

    const importXmlFile = async (file) => {
        if (!file) return;
        setImportBusy(true); setImportResult(null);
        try {
            // Use raw fetch to send the binary body directly — axios may
            // re-serialise it through JSON.stringify and corrupt the XML.
            const res = await fetch(`${BACKEND_URL}/api/sms/import-xml`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/xml" },
                body: file,
            });
            const j = await res.json();
            if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
            setImportResult(j.summary);
            await load();
        } catch (e) {
            setImportResult({ error: String(e.message || e) });
        } finally {
            setImportBusy(false);
            if (xmlRef.current) xmlRef.current.value = "";
        }
    };

    const downloadApk = async () => {
        try {
            const res = await api.get("/sms/apk/download", { responseType: "blob" });
            const pin = res.headers["x-pairing-pin"];
            const pairing_id = res.headers["x-pairing-id"];
            const filename = apk?.filename || "paygateway-sms.zip";
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            if (pin) setPairing({ pin, pairing_id });
        } catch (e) {
            const msg = e?.response?.status ? `Download failed (HTTP ${e.response.status})` : "Download failed — please try again";
            alert(msg);
        }
    };

    return (
        <div className="space-y-5 max-w-5xl">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">SMS verification</h1>
                    <p className="text-sm text-slate-500 mt-1">Whitelist bank SMS sender IDs. The forwarder app auto-approves matching transactions.</p>
                </div>
                <button data-testid="add-sender" onClick={() => setOpen(true)} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add sender ID</button>
            </div>

            {/* APK download card — partner-customized */}
            {apk && (
                <div className="card p-5 border-blue-200 bg-gradient-to-br from-blue-50/50 to-emerald-50/40">
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center text-white shrink-0">
                            <Smartphone className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-base">PayGateway SMS Forwarder</h3>
                                {apk.delivery_kind === "apk"
                                    ? <span className="pill pill-success">APK</span>
                                    : <span className="pill pill-info">Setup Bundle</span>}
                                <span className="pill pill-muted">For {apk.business_name}</span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
                                {apk.delivery_kind === "apk"
                                    ? "Install this APK on a dedicated Android phone. Open the app once and either scan your QR code or paste the forwarder URL below — every incoming bank SMS will be forwarded for auto-approval."
                                    : "Download this Setup Bundle (config + QR + README) and use any SMS-forwarder app from the Play Store / F-Droid pointed at your unique URL."}
                            </p>
                            <div className="text-[11px] font-mono text-slate-500 mt-2 break-all">{apk.forwarder_url}</div>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0 sm:flex-row">
                            <button data-testid="show-qr" onClick={() => setShowQr(true)} className="btn-secondary px-3 py-2 rounded-md text-sm flex items-center gap-2"><QrCode className="w-4 h-4" /> QR</button>
                            <button data-testid="apk-download" onClick={downloadApk} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Download {apk.delivery_kind === "apk" ? "APK" : "Bundle"}</button>
                        </div>
                    </div>

                    {apk.delivery_kind === "apk" && (
                        <div className="mt-5 pt-4 border-t border-blue-100/70">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Setup on your phone</div>
                            <ol className="text-sm text-slate-700 space-y-2 leading-relaxed">
                                <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>Install the APK on a dedicated Android phone (enable <em>Install from unknown sources</em> if prompted).</li>
                                <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>Open the app → tap <strong>HTTP callback</strong>.</li>
                                <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>Paste the forwarder URL above (or scan the QR), keep method = <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px]">POST</code>.</li>
                                <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">4</span>Set the JSON payload template to:
                                    <code className="block mt-1 ml-7 bg-slate-100 px-2 py-1 rounded text-[11px] font-mono">{"{ \"sender\": \"%from%\", \"body\": \"%text%\", \"received_at\": \"%sentStamp%\" }"}</code>
                                </li>
                                <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">5</span>Tap <strong>Save</strong> (button turns green) and grant the SMS-receive permission.</li>
                            </ol>
                            <p className="text-[11px] text-slate-500 mt-3">Source: open-source <a href="https://github.com/optimalstrategy/sms_forwarder_app" target="_blank" rel="noreferrer" className="underline">sms_forwarder_app</a>. The APK forwards every incoming SMS to your unique URL — only whitelisted sender IDs trigger auto-approval, others are logged and ignored.</p>
                        </div>
                    )}
                </div>
            )}

            <div className="card p-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Manual setup (any SMS-forwarder app)</div>
                <p className="text-sm text-slate-600 mb-3">Configure your SMS-forwarder Android app to POST each whitelisted SMS to this URL:</p>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 font-mono text-xs break-all">{apk?.forwarder_url || "—"}</div>
                <p className="text-xs text-slate-500 mt-2">Body template: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{"{ \"sender\": \"%from%\", \"body\": \"%text%\", \"received_at\": \"%sentStamp%\" }"}</code></p>
                {apk?.forwarder_url && !apk.forwarder_url.startsWith("https://") && (
                    <div className="mt-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
                        <strong>⚠ HTTPS required:</strong> the URL above is <code className="font-mono">http://</code>. Most Android SMS-forwarder apps drop the request body when following http→https redirects. Update your forwarder to use <code className="font-mono">https://</code>.
                    </div>
                )}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button data-testid="sms-test-webhook-btn"
                            onClick={testWebhook}
                            disabled={testBusy}
                            className="btn-secondary px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-60">
                        {testBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                        Test webhook now
                    </button>
                    <span className="text-[11px] text-slate-400">Sends a synthetic SMS through your URL to verify reachability.</span>
                </div>
                {testResult && (
                    <div data-testid="sms-test-webhook-result"
                         className={`mt-2 text-[11px] rounded border p-2.5 leading-relaxed ${
                            testResult.ok
                                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                : "bg-rose-50 border-rose-200 text-rose-800"
                         }`}>
                        <div className="flex items-center gap-1.5 font-semibold">
                            {testResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                            {testResult.ok ? "Webhook is reachable" : "Webhook test failed"}
                            {testResult.status_code != null && <span className="font-mono ml-1">HTTP {testResult.status_code}</span>}
                        </div>
                        {testResult.hint && <div className="mt-1">{testResult.hint}</div>}
                        {testResult.response && (
                            <pre className="mt-1 font-mono text-[10px] bg-white/60 px-2 py-1 rounded overflow-x-auto whitespace-pre-wrap break-all">{typeof testResult.response === "string" ? testResult.response : JSON.stringify(testResult.response)}</pre>
                        )}
                    </div>
                )}
            </div>

            <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-blue-600" /><span className="font-semibold text-sm">Whitelisted senders</span></div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                        <tr><th className="px-4 py-2.5 text-left">Sender ID</th><th className="px-4 py-2.5 text-left">Bank</th><th className="px-4 py-2.5 text-left">Note</th><th></th></tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No senders whitelisted yet.</td></tr>}
                        {items.map((i) => (
                            <tr key={i.id} className="border-b border-slate-100 last:border-0">
                                <td className="px-4 py-3 font-mono text-xs">{i.sender_id}</td>
                                <td className="px-4 py-3">{i.bank_label || "—"}</td>
                                <td className="px-4 py-3 text-slate-600">{i.note || "—"}</td>
                                <td className="px-4 py-3 text-right"><button onClick={() => remove(i.id)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded"><Trash2 className="w-4 h-4" /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="card p-5">
                <div className="flex items-start gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700 shrink-0">
                        <RefreshCw className="w-4 h-4" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-base">Bulk import SMS history (XML)</h3>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                            If your live forwarder missed some SMS (battery optimisation, sender filter, etc.), pull a complete dump from your phone and upload it here. Every SMS is processed through the same matching pipeline — duplicates are skipped automatically, so you can re-upload any time.
                        </p>
                    </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-700 mb-3 leading-relaxed">
                    <div className="font-semibold text-slate-900 mb-1.5">How to export from your phone (5 min):</div>
                    <ol className="space-y-1 list-decimal list-inside">
                        <li>Install <strong>SMS Backup &amp; Restore</strong> by SyncTech (free, Play Store)</li>
                        <li>Tap <strong>Set up a backup</strong> → enable only <strong>SMS messages</strong> → choose <strong>Local backup only</strong></li>
                        <li>Tap <strong>Back up now</strong> → it produces a file like <code className="bg-slate-200 px-1 py-0.5 rounded">SMS-Backup-…xml</code></li>
                        <li>Email / AirDrop / share that XML file to this device, then upload it below</li>
                    </ol>
                </div>
                <input ref={xmlRef} data-testid="sms-import-xml" type="file" accept=".xml,application/xml,text/xml"
                    onChange={(e) => importXmlFile(e.target.files?.[0])} className="hidden" />
                <button
                    data-testid="sms-import-btn"
                    type="button"
                    onClick={() => xmlRef.current?.click()}
                    disabled={importBusy}
                    className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
                    {importBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Upload className="w-4 h-4" /> Upload XML file</>}
                </button>
                {importResult && (
                    <div data-testid="sms-import-summary" className={`mt-3 rounded-md p-3 text-xs ${importResult.error ? "bg-rose-50 border border-rose-200 text-rose-800" : "bg-emerald-50 border border-emerald-200 text-emerald-900"}`}>
                        {importResult.error ? (
                            <span>Import failed: {importResult.error}</span>
                        ) : (
                            <>
                                <div className="font-medium mb-1">Imported {importResult.imported} of {importResult.total} SMS · {importResult.duplicates} duplicate{importResult.duplicates === 1 ? "" : "s"} skipped</div>
                                <div className="text-[11px] text-emerald-800/80 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <div><strong>{importResult.auto_approved}</strong> auto-approved</div>
                                    <div><strong>{importResult.matched_no_approve}</strong> matched only</div>
                                    <div><strong>{importResult.logged_only}</strong> logged</div>
                                    {importResult.errors?.length ? <div><strong>{importResult.errors.length}</strong> errors</div> : null}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-emerald-600" /><span className="font-semibold text-sm">Recent SMS logs</span></div>
                {logs.length === 0 && <div className="px-4 py-8 text-center text-slate-400 text-sm">No SMS received yet.</div>}
                <div className="max-h-80 overflow-y-auto">
                    {logs.map((l) => (
                        <div key={l.id} className="px-5 py-3 border-b border-slate-100 last:border-0 text-sm">
                            <div className="flex items-center justify-between">
                                <div className="font-mono text-xs">{l.sender}</div>
                                <div className="flex gap-1.5">
                                    {l.whitelisted ? <span className="pill pill-info">whitelisted</span> : <span className="pill pill-muted">not-whitelisted</span>}
                                    {l.auto_approved ? <span className="pill pill-success">auto-approved</span> : null}
                                </div>
                            </div>
                            <div className="text-xs text-slate-600 mt-1 line-clamp-2">{l.body}</div>
                            <div className="text-[10px] text-slate-400 mt-1 font-mono">
                                {shortDate(l.logged_at)}
                                {l.parsed_amount != null && ` · ₹${l.parsed_amount}`}
                                {l.parsed_utr && ` · UTR ${l.parsed_utr}`}
                                {l.matched_txn_id && ` · matched ${l.matched_txn_id.slice(-8)}`}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {open && <AddSenderModal onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
            {showQr && apk && <ForwarderQrModal forwarderUrl={apk.forwarder_url} onClose={() => setShowQr(false)} />}
            {pairing && <PairingPinModal pin={pairing.pin} pairingId={pairing.pairing_id} onClose={() => setPairing(null)} />}
        </div>
    );
}

function PairingPinModal({ pin, pairingId, onClose }) {
    const [copied, setCopied] = useState(false);
    const copy = () => { navigator.clipboard.writeText(pin); setCopied(true); setTimeout(() => setCopied(false), 1500); };
    return (
        <Modal testId="pairing-pin-modal" onClose={onClose} title="Your one-time pairing PIN" size="md">
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                ⚠ This PIN is shown <strong>only once</strong>. Keep it ready — your forwarder app will ask for it on first launch. Without it, the bundle can&apos;t be activated.
            </div>
            <div className="text-center my-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Pairing PIN</div>
                <div data-testid="pairing-pin-value" className="font-mono text-5xl font-bold tracking-[0.4em] text-slate-900 select-all py-3">{pin}</div>
                <button data-testid="pairing-copy-btn" onClick={copy} className="btn-secondary px-4 py-2 rounded-md text-sm mt-2">{copied ? "✓ Copied" : "Copy PIN"}</button>
            </div>
            <div className="text-xs text-slate-500 mt-4 space-y-1">
                <div>Pairing ID: <span className="font-mono">{pairingId}</span></div>
                <div>Valid for 30 days. After successful pairing, this PIN is consumed and can&apos;t be reused.</div>
                <div>Lost the PIN? Just download the bundle again — a new PIN is issued every time.</div>
            </div>
            <div className="mt-5 flex justify-end">
                <button data-testid="pairing-done-btn" onClick={onClose} className="btn-primary px-5 py-2 rounded-md text-sm">I&apos;ve saved it</button>
            </div>
        </Modal>
    );
}

function ForwarderQrModal({ forwarderUrl, onClose }) {
    // Use a public-good QR generator (qr-server.com) for client-side rendering.
    // Fully URL-encoded so it works for long URLs.
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(forwarderUrl)}`;
    return (
        <Modal testId="forwarder-qr-modal" onClose={onClose} title="Scan to configure forwarder">
            <div className="text-center">
                <div className="inline-block bg-white p-3 rounded-lg ring-2 ring-blue-100">
                    <img src={qrSrc} alt="forwarder URL QR" className="w-64 h-64" data-testid="forwarder-qr-image" />
                </div>
                <p className="text-xs text-slate-500 mt-3 break-all font-mono">{forwarderUrl}</p>
                <p className="text-xs text-slate-500 mt-2">Open your SMS-forwarder app → Add new endpoint → Scan QR.</p>
            </div>
        </Modal>
    );
}

function AddSenderModal({ onClose, onDone }) {
    const [f, setF] = useState({ sender_id: "", bank_label: "", note: "" });
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try { await api.post("/sms/whitelist", f); onDone(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <Modal testId="add-sender-modal" onClose={onClose} title="Whitelist a sender ID">
            <form onSubmit={submit} className="space-y-3">
                <div><label className="label">Sender ID</label>
                    <input data-testid="ws-sender" required minLength={2} maxLength={20} className="input font-mono uppercase" value={f.sender_id} onChange={(e) => setF({ ...f, sender_id: e.target.value.toUpperCase() })} placeholder="VK-HDFCBK" />
                    <div className="text-[11px] text-slate-500 mt-1">Indian SMS sender ID format (DLT). E.g. VK-HDFCBK, AD-PAYTM, JD-AXISBK.</div>
                </div>
                <div><label className="label">Bank label (optional)</label><input data-testid="ws-bank" className="input" value={f.bank_label} onChange={(e) => setF({ ...f, bank_label: e.target.value })} placeholder="HDFC Bank" /></div>
                <div><label className="label">Note (optional)</label><input data-testid="ws-note" className="input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
                {err && <div className="text-sm text-rose-600">{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md text-sm">Cancel</button>
                    <button data-testid="ws-submit" type="submit" disabled={busy} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Add</button>
                </div>
            </form>
        </Modal>
    );
}

export function BankStatements() {
    const [items, setItems] = useState([]);
    const [pending, setPending] = useState([]);
    const [busy, setBusy] = useState(false);
    const [bankLabel, setBankLabel] = useState("");
    const [note, setNote] = useState("");
    const [matchModal, setMatchModal] = useState(null); // { stmtId }
    const fileRef = useRef(null);

    const load = async () => {
        const [s, t] = await Promise.all([
            api.get("/partner/statements"),
            api.get("/partner/transactions?status=submitted"),
        ]);
        setItems(s.data.items); setPending(t.data.items);
    };
    useEffect(() => { load(); }, []);

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("bank_label", bankLabel);
            fd.append("note", note);
            await api.post("/partner/statements", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setBankLabel(""); setNote("");
            load();
        } catch (e) { alert(formatDetail(e?.response?.data?.detail) || "Upload failed"); }
        finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const remove = async (id) => { if (!window.confirm("Delete this statement?")) return; await api.delete(`/partner/statements/${id}`); load(); };
    const downloadStmt = async (id, filename) => {
        try {
            const res = await api.get(`/partner/statements/${id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
        } catch { alert("Download failed"); }
    };
    const manualApprove = async (txnId, sid) => {
        if (!window.confirm(`Manually approve this transaction using statement?`)) return;
        try {
            await api.post(`/partner/statements/${sid}/manual-approve`, { txn_id: txnId, note: "Verified via uploaded statement" });
            load();
            setMatchModal(null);
        } catch (e) { alert(formatDetail(e?.response?.data?.detail)); }
    };
    const autoMatch = async (sid) => {
        if (!window.confirm("Auto-match this statement against all pending payments? Matched UTRs will be approved instantly.")) return;
        try {
            const r = await api.post(`/partner/statements/${sid}/auto-match`, {});
            const d = r.data;
            alert(`Parsed ${d.rows_parsed} rows · ${d.approved} payment(s) auto-approved.${d.approved < d.rows_parsed ? "\n\nUnmatched rows are left in the statement detail panel." : ""}`);
            load();
        } catch (e) {
            alert(formatDetail(e?.response?.data?.detail) || "Auto-match failed");
        }
    };

    return (
        <div className="space-y-5 max-w-5xl">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Bank statements & manual approval</h1>
                <p className="text-sm text-slate-500 mt-1">Upload bank statements (PDF/CSV) to reconcile transactions. Manually approve via the matched statement.</p>
            </div>

            <div className="card p-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Upload statement</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><label className="label">Bank label</label><input data-testid="stmt-bank" className="input" value={bankLabel} onChange={(e) => setBankLabel(e.target.value)} placeholder="HDFC Bank" /></div>
                    <div><label className="label">Note</label><input data-testid="stmt-note" className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Statement for Apr 2026" /></div>
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.csv,.xlsx,.xls,image/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="stmt-file" />
                <button data-testid="stmt-upload" onClick={() => fileRef.current?.click()} disabled={busy} className="btn-secondary px-4 py-2 rounded-md text-sm mt-4 flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Choose file</button>
                <div className="text-[11px] text-slate-500 mt-2">PDF / CSV / XLSX / image up to 10 MB</div>
            </div>

            <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /><span className="font-semibold text-sm">Uploaded statements</span></div>
                {items.length === 0 && <div className="px-4 py-8 text-center text-slate-400 text-sm">No statements uploaded.</div>}
                <table className="w-full text-sm">
                    <tbody>
                        {items.map((s) => (
                            <tr key={s.id} className="border-b border-slate-100 last:border-0">
                                <td className="px-5 py-3"><div className="font-medium">{s.filename}</div><div className="text-xs text-slate-500">{s.bank_label || "—"} · {(s.size / 1024).toFixed(1)} KB</div></td>
                                <td className="px-4 py-3 text-xs text-slate-500">{shortDate(s.uploaded_at)}</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button data-testid={`stmt-automatch-${s.id}`} onClick={() => autoMatch(s.id)} className="btn-primary px-3 py-1.5 rounded-md text-xs">Auto-match</button>
                                        <button data-testid={`stmt-match-${s.id}`} onClick={() => setMatchModal({ stmtId: s.id })} className="btn-emerald px-3 py-1.5 rounded-md text-xs">Match & approve</button>
                                        <button onClick={() => downloadStmt(s.id, s.filename)} className="btn-secondary px-2.5 py-1.5 rounded-md"><Download className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => remove(s.id)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {matchModal && (
                <Modal testId="match-modal" onClose={() => setMatchModal(null)} title="Match a pending transaction" size="lg">
                    {pending.length === 0 ? (
                        <div className="text-sm text-slate-500 text-center py-6">No submitted (pending verification) transactions.</div>
                    ) : (
                        <div className="space-y-2">
                            {pending.map((t) => (
                                <div key={t.txn_id} className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:bg-slate-50">
                                    <div>
                                        <div className="font-medium text-sm">{t.customer_username}</div>
                                        <div className="text-xs text-slate-500 font-mono">UTR: {t.utr || "—"} · {fmtINR(t.amount)}</div>
                                    </div>
                                    <button data-testid={`match-approve-${t.txn_id}`} onClick={() => manualApprove(t.txn_id, matchModal.stmtId)} className="btn-emerald px-3 py-1.5 rounded-md text-xs">Approve</button>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}
